import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "./icons";
import { Card, Spinner, cx } from "./ui";
import { useBilling, useGitConnections } from "../lib/queries";
import { ConnectSigninHost, useSigninHostCandidate } from "./ConnectSigninHost";

/**
 * What has to be true before a first deployment can succeed.
 *
 * This exists because the alternative is worse: a new customer picks a
 * repository, chooses a plan, fills in a form, and only on submit learns their
 * balance is zero. The prerequisites are knowable up front, so they are shown
 * up front.
 *
 * Credit is conditional: it only matters where payments are configured — a
 * deployment without a provider has no way to acquire any, so demanding it
 * would be a step nobody could complete. A git host is not optional any more.
 * The create form offers repositories only, so an organization with no host
 * connected has nothing to pick from; the picker says so on the form, and the
 * checklist says so here, before anyone opens it.
 */

export interface Prerequisites {
  loading: boolean;
  /** Credit is required and absent. Blocks creating a workload. */
  needsCredit: boolean;
  /** No git host connected. Nothing to deploy from until one is. */
  needsGitConnection: boolean;
  /** Nothing further is required for a first deployment to be accepted. */
  ready: boolean;
}

export function usePrerequisites(orgId: string | undefined): Prerequisites {
  const billing = useBilling(orgId);
  const connections = useGitConnections(orgId);

  const loading = billing.isLoading || connections.isLoading;

  // Only when payments exist. Otherwise credit is not a concept the customer
  // can act on, and the API does not enforce it either.
  const needsCredit = Boolean(billing.data?.topUpAvailable) && (billing.data?.balanceMicros ?? 0) <= 0;

  return {
    loading,
    needsCredit,
    needsGitConnection: (connections.data ?? []).length === 0,
    ready: !loading && !needsCredit,
  };
}

/*
 * A step is a key and three facts about it; the words are `create.json` under
 * `started`, named `<key>`, `<key>Lead` and `<key>Action`.
 */
interface Step {
  key: "credit" | "git" | "deploy";
  href: string;
  done: boolean;
  optional: boolean;
}

/**
 * The checklist.
 *
 * It used to be the empty state for the apps list, which meant it vanished the
 * moment a first app existed — exactly when someone is still learning the
 * platform and has most likely not connected a git host yet. Now it sits above
 * the list and removes itself only once every required step is genuinely done,
 * so the guidance outlasts the first deploy rather than the first row.
 */
export function GettingStarted({
  orgId,
  canCreate,
  hasApps,
}: {
  orgId: string | undefined;
  canCreate: boolean;
  /** Marks the deploy step complete. Without it the list never finishes. */
  hasApps: boolean;
}) {
  const { t } = useTranslation("create");
  const billing = useBilling(orgId);
  // Signed in with a host this organization has not connected: the git step
  // offers that connection directly instead of the generic link.
  const signinHost = useSigninHostCandidate(orgId);
  const connections = useGitConnections(orgId);
  const prerequisites = usePrerequisites(orgId);

  if (prerequisites.loading) {
    return (
      <Card className="flex items-center gap-2.5 p-5 text-sm text-fg-muted">
        <Spinner className="size-4" />
        {t("started.checking")}
      </Card>
    );
  }

  const steps: Step[] = [];

  if (billing.data?.topUpAvailable) {
    // "About a week" was true of the old $5 tier and "about three days" of the
    // $8 one. Migration 0102 put the floor at $2, so a dollar now buys 336
    // hours — two weeks. Rounded down, because this is a promise about
    // someone's money and the safe direction is under.
    steps.push({
      key: "credit",
      href: "/settings/billing",
      done: (billing.data?.balanceMicros ?? 0) > 0,
      optional: false,
    });
  }

  steps.push({
    key: "git",
    href: "/settings/integrations",
    done: (connections.data ?? []).length > 0,
    optional: false,
  });

  steps.push({
    key: "deploy",
    href: "/apps/new",
    done: hasApps,
    optional: false,
  });

  // Nothing required is outstanding, so stop taking up the top of the page.
  // Optional steps alone are not worth a permanent panel.
  if (steps.every((step) => step.done || step.optional)) return null;

  const remaining = steps.filter((step) => !step.done && !step.optional).length;

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-fg">{t("started.heading")}</h2>
        <span className="micro">{t("started.remaining", { count: remaining })}</span>
      </div>

      <ol className="mt-6 space-y-5">
        {steps.map((step, index) => {
          // The step to act on now: the first that is neither done nor merely
          // suggested. Highlighting more than one would defeat the point.
          const isNext =
            !step.done && steps.slice(0, index).every((earlier) => earlier.done || earlier.optional);

          return (
            <li key={step.key} className="flex gap-4">
              <span
                className={cx(
                  "mt-0.5 grid size-6 shrink-0 place-items-center text-xs tabular-nums",
                  step.done
                    ? "bg-mint text-canvas"
                    : isNext
                      ? "bg-accent text-white"
                      : "border border-line text-fg-subtle",
                )}
              >
                {step.done ? <Icon name="check" className="size-3.5" /> : index + 1}
              </span>

              <div className="min-w-0 flex-1">
                <p className={cx("text-sm", step.done ? "text-fg-subtle" : "text-fg")}>
                  {t(`started.${step.key}` as "started.credit")}
                  {step.optional && !step.done && (
                    <span className="micro ml-2.5">{t("started.optional")}</span>
                  )}
                </p>

                {!step.done && (
                  <>
                    <p className="mt-1 text-sm leading-6 text-fg-muted">
                      {t(`started.${step.key}Lead` as "started.creditLead")}
                    </p>
                    {step.key === "git" && signinHost && (
                      <div className="mt-2.5">
                        <ConnectSigninHost orgId={orgId} canManage={canCreate} redirectTo="/apps" compact />
                      </div>
                    )}
                    {canCreate && !(step.key === "git" && signinHost) && (
                      <Link
                        to={step.href}
                        className={cx(
                          "mt-2.5 inline-flex items-center gap-1.5 text-sm transition-colors duration-200 ease-smooth",
                          isNext
                            ? "text-accent hover:text-accent-hover"
                            : "text-fg-subtle hover:text-fg",
                        )}
                      >
                        {t(`started.${step.key}Action` as "started.creditAction")}
                        <Icon name="chevron" className="size-3.5" />
                      </Link>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}
